import * as React from "react"
import { useTranslation } from "react-i18next"

import { useUIPreferences } from "Catalog/shared/components/UIPreferencesContext"
import useCatalogNavigation from "Catalog/shared/hooks/useCatalogNavigation"
import StatefulField from "shared/components/StatefulField"
import useEditor from "shared/hooks/useEditor"
import useHandleImageUpload from "shared/hooks/useHandleImageUpload"
import { CategoryId } from "shared/hubrise/ids"
import { ICategory, ICategoryOrProduct, IProduct } from "shared/hubrise/models"
import { TagOption } from "shared/hubrise/utils/tags"
import { allTaxCombinations, TaxCombination } from "shared/hubrise/utils/tax"

import { useBulkTaxUpdateLabel } from "../BulkTaxUpdateLabel"
import FoldableSection from "../FoldableSection"
import { Option, OptionLabel, OptionValue } from "../Styles"

interface GeneralProps {
  product: IProduct
  tagOptions: Array<TagOption>
}

const General = ({ product, tagOptions }: GeneralProps): JSX.Element => {
  const { t } = useTranslation()
  const { editor, catalog } = useEditor()
  const { catalogNavigation } = useCatalogNavigation()
  const handleImageUpload = useHandleImageUpload()
  const { generalExpanded, setGeneralExpanded } = useUIPreferences()

  const handleChange =
    <TField extends keyof ICategoryOrProduct>(field: TField, options?: { collapse?: boolean }) =>
    (value: (ICategory & IProduct)[TField]) =>
      editor.categoryOrProduct.update(product, field, value, options)

  const bulkTaxUpdate = useBulkTaxUpdateLabel(product, (categoryId: CategoryId | null) =>
    editor.product.bulkUpdateTaxRate(product.taxRate, product.id, categoryId),
  )

  const renderTagOption = ({ label, value }: { label: string; value: string }) => (
    <Option>
      <OptionLabel>{label}</OptionLabel>
      <OptionValue>{value}</OptionValue>
    </Option>
  )

  return (
    <FoldableSection
      title={t("product.general_title")}
      expanded={generalExpanded}
      onToggle={() => setGeneralExpanded(!generalExpanded)}
    >
      <StatefulField.TextEditor
        name="product-description"
        initialValue={product.description}
        label={t("product.description.label")}
        withMargin
        placeholder={t("product.description.placeholder.product")}
        onChange={handleChange("description")}
      />

      <StatefulField.Select
        initialValue={product.tags}
        label={t("product.tags")}
        withMargin
        options={tagOptions}
        onCreateOption={(label, afterCreateCallback) => afterCreateCallback(label)}
        onChange={handleChange("tags")}
        isMulti
        sortValuesForDisplay={true}
        sortValuesForSave={true}
        withClearValue={true}
        renderOption={renderTagOption}
      />

      <StatefulField.TaxSelector
        initialValue={product.taxRate}
        label={bulkTaxUpdate.label}
        withMargin
        allValues={allTaxCombinations(catalog)}
        onChange={(taxRate: TaxCombination | null) => {
          editor.product.update(product.id, "taxRate", taxRate, {})
          bulkTaxUpdate.onChange()
        }}
      />

      <StatefulField.ImagesHolder
        name="product-images"
        initialValue={product.imageHrIds}
        label={t("product.images")}
        withMargin
        onChange={handleChange("imageHrIds", { collapse: false })}
        onUpload={handleImageUpload}
        imagePathResolver={(imageHrId) => catalogNavigation.imagePath(imageHrId)}
        modalTitle={product.name}
      />
    </FoldableSection>
  )
}

export default General
